import { Autocomplete, type AutocompleteProps, TextField, type TextFieldProps } from '@mui/material';
import React, { type CSSProperties, useRef } from 'react';

import { useScroll } from '../hooks/useScroll';
import { type IAutocompleteOption } from '../models/IAutocompleteOption';

interface CAutocompleteProps
  extends Pick<AutocompleteProps<any, any, any, any>, 'multiple'>,
    Pick<TextFieldProps, 'label'> {
  textFieldLabel?: string;
  placeholder?: string;
  options: IAutocompleteOption[];
  value: IAutocompleteOption[];
  onChangeSingle?: (option: IAutocompleteOption) => void;
  onChangeMultiple?: (options: IAutocompleteOption[]) => void;
  onClick?: (ev: React.MouseEvent) => void;

  /**
   * @deprecated Use onChangeSingle or onChangeMultiple
   */
  onChange?: (options: IAutocompleteOption[]) => void;

  /**
   * @deprecated Prefer onChangeMultiple to imply multiple
   */
  multiple?: boolean;
  style?: Pick<CSSProperties, 'flexGrow'>;
}

export default function CAutocomplete(props: CAutocompleteProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = (ev: Event) => {
    const target = ev.target as HTMLElement;
    if (target.querySelector('ul') && ref.current) {
      const input = ref.current.querySelector('input');
      (input as HTMLInputElement)?.blur();
    }
  };

  useScroll({ target: 'document', useCapture: true, onScroll: handleScroll });

  const isMultiple = Boolean(props.onChangeMultiple) || Boolean(props.multiple);

  const formattedValue = isMultiple ? props.value : (props.value?.[0] ?? null);

  const handleChange = (_ev: React.SyntheticEvent, val: IAutocompleteOption | IAutocompleteOption[] | null) => {
    if (isMultiple) {
      const arr = (val as IAutocompleteOption[]) ?? [];
      if (props.onChangeMultiple) {
        props.onChangeMultiple(arr);
      } else if (props.onChange) {
        props.onChange(arr);
      }
    } else {
      const opt = (val as IAutocompleteOption | null) ?? undefined;
      if (opt && props.onChangeSingle) {
        props.onChangeSingle(opt);
      } else if (props.onChange) {
        props.onChange(opt ? [opt] : []);
      }
    }
  };

  return (
    <div onClick={props.onClick}>
      <Autocomplete
        {...props}
        ref={ref}
        multiple={isMultiple}
        disablePortal={true}
        slotProps={{ popper: { sx: { zIndex: 10 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props.textFieldLabel}
            placeholder={props.placeholder}
            variant={props.textFieldLabel ? 'filled' : 'outlined'}
          />
        )}
        value={formattedValue}
        onChange={handleChange}
        isOptionEqualToValue={(o, v) => {
          return o.key === v.key;
        }}
        getOptionLabel={(o) => o.label}
      />
    </div>
  );
}
